import { App_Url_New, Roles } from "@/shared/common/static";
import { openModal } from "@/shared/redux/reducers/uiSlice";
import { useAppDispatch } from "@/shared/redux/store/hooks";
import { usePosterReducers } from "@/shared/redux/store/useGetData";

const CHATBOT_BASE_URL =
  import.meta.env.VITE_APP_CHATBOT_URL || "https://insurehubrag.nexsysit.co.in";

const getChatbotBaseUrl = () => CHATBOT_BASE_URL.replace(/\/+$/, "");

function getChatbotUrlForRole(roleName: unknown) {
  const normalizedRole = String(roleName || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const isAdminRole = normalizedRole === Roles.ADMIN;
  const rolePath = isAdminRole ? "" : "/agent-dashboard";

  const baseUrl = getChatbotBaseUrl();
  return rolePath ? `${baseUrl}${rolePath}` : baseUrl;
}

function addCacheBuster(chatbotUrl: string) {
  const url = new URL(chatbotUrl);
  url.searchParams.set("t", String(Date.now()));
  return url.toString();
}

type ChatbotAuthParams = {
  token?: string;
  agentName?: string;
};

function addChatbotAuthParams(chatbotUrl: string, authParams: ChatbotAuthParams) {
  const url = new URL(chatbotUrl);

  if (authParams.token) {
    url.searchParams.set("token", authParams.token);
  }

  if (authParams.agentName) {
    url.searchParams.set("agentName", authParams.agentName);
  }

  return url.toString();
}

// Shared "Ask Layla" launcher used by the floating button (public pages) and
// the auth header button (logged-in B2C/ADMIN pages).
export function useOpenChatbot() {
  const { auth: user } = usePosterReducers();
  const dispatch = useAppDispatch();
  const isAuthenticated = Boolean(user.user?.access_token);
  const authenticatedUser = user.user as any;
  const backendSignedToken = authenticatedUser?.access_token || "";
  const role = authenticatedUser?.role || authenticatedUser?.user?.role;
  const roleName =
    role?.name || role?.display_name || role?.role_name || (typeof role === "string" ? role : "");
  const agentName = authenticatedUser?.user?.full_name;
  const chatbotAuthParams: ChatbotAuthParams = {
    token: backendSignedToken,
    agentName,
  };

  const chatbotUrl = getChatbotUrlForRole(roleName);
  const normalizedRole = String(roleName || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const isAdmin = normalizedRole === Roles.ADMIN;

  const openChatbot = () => {
    const iframeUrl = !isAuthenticated
      ? getChatbotBaseUrl()
      : isAdmin
        ? undefined
        : addCacheBuster(addChatbotAuthParams(chatbotUrl, chatbotAuthParams));

    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.OPEN_CHAT_BOT,
        data: {
          iframeUrl,
          chatbotOptions:
            isAuthenticated && isAdmin
              ? [
                  {
                    label: "Document Upload",
                    url: addCacheBuster(
                      addChatbotAuthParams(`${getChatbotBaseUrl()}/admin`, chatbotAuthParams)
                    ),
                  },
                  {
                    label: "Agent Monitoring",
                    url: addCacheBuster(
                      addChatbotAuthParams(`${getChatbotBaseUrl()}/super-admin`, chatbotAuthParams)
                    ),
                  },
                ]
              : undefined,
        },
      })
    );
  };

  return { openChatbot, isAuthenticated };
}
